import { Box, TextField } from "@mui/material";
import { ComponentProps } from "../types";
import { CSSProperties, useCallback, useContext, useMemo, useRef, useState } from "react";
import DashboardPageContext from "../DashboardPageContext";
import ExecutionTypeContext, { Mode } from "../ExecutionTypeContext";
import DashboardEnvContext from "../DashboardEnvContext";
import Editor, { Monaco } from "@monaco-editor/react";
import * as monaco from "monaco-editor";

function Component({ component, evaluteScript }: ComponentProps) {
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const dashboard = useContext(DashboardEnvContext);
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const { context, params, updateContext } = useContext(DashboardPageContext);
	const exeMode = useContext(ExecutionTypeContext);
	const [internalValue, setInternalValue] = useState<string | undefined>(component.properties["binding"] ? (evaluteScript(component.properties["binding"]) as string) : undefined);
	const [cachedValue, setCachedValue] = useState<string | undefined>(component.properties["binding"] ? (evaluteScript(component.properties["binding"]) as string) : undefined);
	const editorRef = useRef<undefined | monaco.editor.IStandaloneCodeEditor>();

	const fireBlur = useCallback(
		(value: string | undefined) => {
			if (cachedValue !== value) {
				const script = component.properties["binding"] + "=" + (value ? "`" + value.replace(/`/g, "\\`").replace(/\$/g, "\\$") + "`" : "undefined");
				evaluteScript(script);
				updateContext(context);
			}
		},
		[context]
	);

	const doBlur = () => {
		if (editorRef.current) {
			editorRef.current.getAction("editor.action.formatDocument")?.run();
			fireBlur(editorRef.current.getValue());
		}
	};

	const style = useMemo(() => {
		const style = {} as CSSProperties;
		if (exeMode?.mode === Mode.DESIGN && component.id === exeMode?.activeComponent?.id) style.border = "1px dashed red";
		if (component.properties && component.properties["height"]) {
			style.height = component.properties["height"];
		}
		if (component.properties && component.properties["minHeight"]) style.minHeight = component.properties["minHeight"];
		if (component.properties && component.properties["maxHeight"]) style.maxHeight = component.properties["maxHeight"];
		if (component.properties["flex"]) style.flex = component.properties["flex"];
		return style;
	}, [component, exeMode]);

	const args = useMemo(() => {
		const args = {} as { [key: string]: unknown };
		if (component.properties["height"]) {
			args["height"] = component.properties["height"];
		}
		return args;
	}, [component, context, updateContext, evaluteScript]);

	const boxArgs = useMemo(() => {
		const args = {} as { [key: string]: unknown };
		if (exeMode?.mode === Mode.DESIGN && exeMode.locatorActivated === true) {
			args["onClick"] = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
				if (exeMode.updateActiveComponent) exeMode.updateActiveComponent(component);
				event.stopPropagation();
			};
		}
		return args;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [component, context]);

	function handleEditorDidMount(editor: monaco.editor.IStandaloneCodeEditor, monaco: Monaco) {
		editorRef.current = editor;
		editorRef.current.getAction("editor.action.formatDocument")?.run();
		editorRef.current.onDidBlurEditorText(() => {
			doBlur();
		});
	}

	return (
		<Box style={style} sx={{ border: "1px solid #8b8b8b" }} {...boxArgs}>
			<Editor
				key={String(context.__CONTEXT_VERSION)}
				{...args}
				defaultLanguage="javascript"
				value={internalValue}
				onMount={handleEditorDidMount}
				onChange={(newValue: string | undefined) => setInternalValue(newValue)}
				options={{
					minimap: { enabled: false },
				}}
			/>
		</Box>
	);
}

export default Component;
